import React, { useEffect, useRef, useState } from "react";
import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiPanel,
  EuiProgress,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTextColor,
  EuiTitle,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { HEALTH_COLOR, HEALTH_ICON, HealthStatus } from "../appliance/health";
import { FleetHealthEntry } from "../appliance/useFleetHealth";
import { waveColor } from "../appliance/waves";
import { alertsThatFit, checkTime, tileNote } from "./wall";

const i18n = "anketa.opsanio.health";

/**
 * Minimum card heights: the grid rows grow beyond them to fill a wall screen
 * when there are few incidents, and scroll when there are many.
 */
export const INCIDENT_CARD_HEIGHT = 200;
// Header, VPC line, bar and one full sensor line.
export const COMPACT_INCIDENT_CARD_HEIGHT = 160;
export const FLEET_TILE_HEIGHT = 48;

const ALERT_ROW_HEIGHT = 24;
const MORE_LINE_HEIGHT = 18;
/** Shown until the list has been measured (and without ResizeObserver). */
const DEFAULT_VISIBLE_ALERTS = 3;

const truncate: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Count size on a wall screen: readable from across the room. */
const WALL_COUNT_FONT_SIZE = 88;

/** One of the four count panels (same build as the appliance overview KPIs). */
export const CountPanel = ({
  status,
  label,
  description,
  count,
  loading,
  large = false,
}: {
  status: HealthStatus;
  label: string;
  description: string;
  count: number;
  loading: boolean;
  /** Kiosk: the number is the point, so it goes far beyond EuiStat's "l". */
  large?: boolean;
}): JSX.Element => {
  const active = count > 0 && status !== "unknown";
  return (
    <EuiPanel hasBorder paddingSize="m">
      <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiIcon
            type={HEALTH_ICON[status]}
            size={large ? "l" : "s"}
            color={count > 0 ? HEALTH_COLOR[status] : "subdued"}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiText size={large ? "m" : "xs"} color="subdued">
            {label}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="s" />
      <EuiStat
        title={
          large ? (
            <span
              style={{
                fontSize: WALL_COUNT_FONT_SIZE,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {count}
            </span>
          ) : (
            `${count}`
          )
        }
        description=""
        titleSize="l"
        titleColor={active ? HEALTH_COLOR[status] : "default"}
        isLoading={loading}
      />
      <EuiText size={large ? "s" : "xs"} color="subdued">
        {description}
      </EuiText>
    </EuiPanel>
  );
};

/** A critical or warning appliance with its alerting sensors. */
export const IncidentCard = ({
  entry,
  isNew,
}: {
  entry: FleetHealthEntry;
  /** Got worse since the previous refresh. */
  isNew: boolean;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { appliance, health, alerts } = entry;
  const status = health.status;
  const counts = health.counts;
  const up = counts?.up ?? 0;
  const operational =
    (counts?.up ?? 0) +
    (counts?.down ?? 0) +
    (counts?.warning ?? 0) +
    (counts?.unusual ?? 0);

  // The card grows with its grid row; show as many sensors as the list fits.
  const listRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([item]) =>
      setListHeight(item.contentRect.height),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const visible =
    listHeight === null
      ? Math.min(alerts.length, DEFAULT_VISIBLE_ALERTS)
      : alertsThatFit(
          listHeight,
          alerts.length,
          ALERT_ROW_HEIGHT,
          MORE_LINE_HEIGHT,
        );
  const shown = alerts.slice(0, visible);
  const more = alerts.length - shown.length;

  return (
    <EuiPanel
      hasBorder
      paddingSize="m"
      color={status === "critical" ? "danger" : "warning"}
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <EuiFlexGroup
        gutterSize="s"
        alignItems="center"
        responsive={false}
        style={{ flexGrow: 0 }}
      >
        <EuiFlexItem grow={false}>
          <EuiIcon
            type={HEALTH_ICON[status]}
            color={HEALTH_COLOR[status]}
            aria-label={translate(`${i18n}.status.${status}`)}
          />
        </EuiFlexItem>
        <EuiFlexItem style={{ minWidth: 0 }}>
          <EuiTitle size="s">
            <h3 style={truncate} title={appliance.displayName}>
              {appliance.displayName || appliance.id}
            </h3>
          </EuiTitle>
        </EuiFlexItem>
        {isNew && (
          <EuiFlexItem grow={false}>
            <EuiBadge color="primary">
              {translate(`${i18n}.badge.new`, { defaultMessage: "New" })}
            </EuiBadge>
          </EuiFlexItem>
        )}
        {appliance.wave && (
          <EuiFlexItem grow={false}>
            <EuiBadge color={waveColor(appliance.wave)}>
              {appliance.wave}
            </EuiBadge>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
      <EuiSpacer size="xs" />
      <EuiFlexGroup gutterSize="s" responsive={false} style={{ flexGrow: 0 }}>
        <EuiFlexItem style={{ minWidth: 0 }}>
          <EuiText size="xs" color="subdued" style={truncate}>
            {appliance.common?.metadata?.name || "-"}
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued">
            {translate(`${i18n}.sensorsUp`, {
              values: { up, total: operational },
              defaultMessage: "{{up}}/{{total}} up",
            })}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiProgress
        value={up}
        max={operational || 1}
        size="s"
        color={HEALTH_COLOR[status]}
        aria-hidden="true"
      />
      <EuiHorizontalRule margin="s" />
      <div
        ref={listRef}
        style={{ flex: "1 1 0", minHeight: 0, overflow: "hidden" }}
      >
        {shown.map((sensor) => {
          const down = sensor.category === "down";
          const time = checkTime(sensor.lastCheck);
          return (
            <EuiFlexGroup
              key={sensor.objid}
              gutterSize="s"
              alignItems="center"
              responsive={false}
              style={{ height: ALERT_ROW_HEIGHT, margin: 0 }}
            >
              <EuiFlexItem grow={false}>
                <EuiIcon
                  type={down ? HEALTH_ICON.critical : HEALTH_ICON.warning}
                  color={down ? HEALTH_COLOR.critical : HEALTH_COLOR.warning}
                  size="s"
                  aria-label={sensor.status}
                />
              </EuiFlexItem>
              <EuiFlexItem style={{ minWidth: 0 }}>
                <EuiText
                  size="s"
                  style={truncate}
                  title={`${sensor.device} › ${sensor.sensor} - ${sensor.message}`}
                >
                  <strong>{sensor.device}</strong>
                  <EuiTextColor color="subdued"> › </EuiTextColor>
                  {sensor.sensor}
                  {sensor.message && (
                    <EuiTextColor color="subdued"> - {sensor.message}</EuiTextColor>
                  )}
                </EuiText>
              </EuiFlexItem>
              {time && (
                <EuiFlexItem grow={false}>
                  <EuiText size="xs" color="subdued">
                    {time}
                  </EuiText>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          );
        })}
        {more > 0 && (
          <EuiText
            size="xs"
            color="subdued"
            style={{ paddingLeft: 20, lineHeight: `${MORE_LINE_HEIGHT}px` }}
          >
            {translate(`${i18n}.moreSensors`, {
              values: { count: more },
              defaultMessage: "+ {{count}} more alerting sensors",
            })}
          </EuiText>
        )}
      </div>
    </EuiPanel>
  );
};

/** A healthy or unmonitored appliance in the fleet grid. */
export const FleetTile = ({
  entry,
  recoveredAt,
  showWave = true,
}: {
  entry: FleetHealthEntry;
  /** Off inside a wave column, where the column header names the wave. */
  showWave?: boolean;
  /** Time label when it was an incident at the previous refresh. */
  recoveredAt: string | null;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { appliance, health } = entry;
  const status = health.status;
  const reason = tileNote(entry);

  let note: string | null = null;
  if (reason === "prtgFailed") {
    note = entry.prtgStatus
      ? translate(`${i18n}.tile.prtgFailedStatus`, {
          values: { status: entry.prtgStatus },
          defaultMessage: "PRTG request failed ({{status}})",
        })
      : translate(`${i18n}.tile.prtgFailed`, {
          defaultMessage: "PRTG request failed",
        });
  } else if (reason === "noName") {
    note = translate(`${i18n}.tile.noName`, { defaultMessage: "no VPC name" });
  } else if (reason === "noSensors") {
    note = translate(`${i18n}.tile.noSensors`, { defaultMessage: "no sensors" });
  } else if (recoveredAt) {
    note = translate(`${i18n}.tile.recovered`, {
      values: { time: recoveredAt },
      defaultMessage: "recovered {{time}}",
    });
  }

  return (
    <EuiPanel
      hasBorder
      paddingSize="s"
      grow={false}
      color={status === "unknown" ? "subdued" : recoveredAt ? "primary" : "plain"}
      style={{
        height: FLEET_TILE_HEIGHT,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
      }}
    >
      <EuiFlexGroup
        gutterSize="s"
        alignItems="center"
        responsive={false}
        style={{ minWidth: 0 }}
      >
        <EuiFlexItem grow={false}>
          <EuiIcon
            type={HEALTH_ICON[status]}
            color={HEALTH_COLOR[status]}
            aria-label={translate(`${i18n}.status.${status}`)}
          />
        </EuiFlexItem>
        <EuiFlexItem style={{ minWidth: 0 }}>
          <EuiText size="s" style={truncate} title={appliance.displayName}>
            {appliance.displayName || appliance.id}
          </EuiText>
          {note && (
            <EuiText size="xs" color="subdued" style={truncate}>
              {note}
            </EuiText>
          )}
        </EuiFlexItem>
        {showWave && appliance.wave && (
          <EuiFlexItem grow={false}>
            <EuiBadge color={waveColor(appliance.wave)}>{appliance.wave}</EuiBadge>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </EuiPanel>
  );
};
